import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiResetPassword } from '../api';
import PasswordInput from '../components/PasswordInput';
import { checkPassword, PASSWORD_MAX, PASSWORD_MIN } from '../lib/passwordPolicy';

/** Public page reached from a password-reset link (/reset-password?token=…). */
export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const check = checkPassword(password);
  const canSubmit = !busy && password.length > 0 && password === confirm && check.ok;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError(t('resetPassword.mismatch')); return; }
    if (!check.ok) return;
    setBusy(true);
    try {
      await apiResetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset the password.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="page-pad" style={{ maxWidth: 420, margin: '60px auto' }}>
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('resetPassword.invalidLink')}</h2>
          <p style={{ color: 'var(--text-dim)' }}>{t('resetPassword.missingToken')}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/login')}>{t('resetPassword.goToSignIn')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ maxWidth: 420, margin: '60px auto' }}>
      <div className="card" style={{ padding: 28 }}>
        <h1 style={{ marginTop: 0 }}>{t('resetPassword.title')}</h1>
        {done ? (
          <>
            <p style={{ color: 'var(--success)' }}>
              {t('resetPassword.changed')}
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/login')}>{t('resetPassword.signIn')}</button>
          </>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label>{t('resetPassword.newPassword')}</label>
              <PasswordInput className="form-input" value={password} required
                onChange={(e) => setPassword(e.target.value)} />
              {password.length === 0 && (
                <small style={{ color: 'var(--text-dim)' }}>
                  {t('resetPassword.pwHint')}
                </small>
              )}
              {password.length > 0 && (
                <div style={{ marginTop: 4 }} data-testid="password-rules">
                  <p style={{ color: check.tooShort || check.tooLong ? 'var(--danger)' : 'var(--success)', fontSize: 'var(--font-xs)', margin: '2px 0' }}>
                    {check.tooShort || check.tooLong ? '✗ ' : '✓ '}
                    {t('passwordRules.length', { min: PASSWORD_MIN, max: PASSWORD_MAX })}
                  </p>
                  <p style={{ color: check.weakMix ? 'var(--danger)' : 'var(--success)', fontSize: 'var(--font-xs)', margin: '2px 0' }}>
                    {check.weakMix ? '✗ ' : '✓ '}
                    {t('passwordRules.mix')}
                  </p>
                  <p style={{ color: check.predictable ? 'var(--danger)' : 'var(--success)', fontSize: 'var(--font-xs)', margin: '2px 0' }}>
                    {check.predictable ? '✗ ' : '✓ '}
                    {t('passwordRules.predictable')}
                  </p>
                </div>
              )}
            </div>
            <div>
              <label>{t('resetPassword.confirmPassword')}</label>
              <PasswordInput className="form-input" value={confirm} required
                onChange={(e) => setConfirm(e.target.value)} />
            </div>
            {error && <div style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{error}</div>}
            <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
              {busy ? t('resetPassword.saving') : t('resetPassword.setNewPassword')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
